import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { IoArrowBackSharp } from "react-icons/io5";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { TbTrash, TbTrashOff } from "react-icons/tb";
import { FaPlus } from "react-icons/fa";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import Select from "react-select";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import swal from "sweetalert";
import AddVendorBankAccountModal from "./AddVendorBankAccountModal";
import {
  CheckPermission,
  customOption,
  customSingleValue,
  LoadingSpinner
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";

// ---------------- Reusable Components ----------------
const CustomSelect = ({
  label,
  name,
  options,
  value,
  onChange,
  loading,
  error,
  placeholder,
  isDisabled,
}) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Select
      components={{ Option: customOption, SingleValue: customSingleValue }}
      options={options}
      name={name}
      value={value}
      className="basic-select"
      classNamePrefix="select"
      isLoading={loading}
      isDisabled={isDisabled || options.length === 0}
      onChange={onChange}
      placeholder={placeholder}
      styles={{
        control: (provided, state) => ({
          ...provided,
          borderColor: error ? "#dc3545" : state.isFocused ? "#1ab394" : "#e5e6e7",
          "&:hover": {
            borderColor: error ? "#dc3545" : state.isFocused ? "#1ab394" : "#d2d2d2",
          },
        }),
      }}
    />
    {error && <h4 className="text-error">{error}</h4>}
  </FormGroup>
);

const CustomInput = ({ label, name, value, onChange, error, ...rest }) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Input
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      className="no-drag"
      invalid={!!error}
      {...rest}
    />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

// ---------------- Helper Functions ----------------
const isSpecialCategory = (categoryCode) => {
  return (
    categoryCode === "return_loan_individual" ||
    categoryCode === "loan_individual" ||
    categoryCode === "return_loan_from_markaz" ||
    categoryCode === "maktaba_payment_to_markaz" ||
    (categoryCode && categoryCode.includes("markaz"))
  );
};

const checkShowBankAccountField = (entries) => {
  if (!entries || entries.length === 0) return false;
  return entries.some((entry) => {
    const code = entry.selectedCategory?.label1;
    return (
      code &&
      code !== "return_loan_from_markaz" &&
      code !== "return_loan_individual" &&
      code !== "maktaba_payment_to_markaz"
    );
  });
};

const categorySupportsQtyPrice = (categoryCode) => {
  return (
    categoryCode === "asset" ||
    categoryCode === "maktaba"
  );
};

// ---------------- Main Component ----------------
const ExpenseFromAmanat = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");

  // ---------- State ----------
  const [loading, setLoading] = useState({
    source: false,
    submit: false,
  });

  const [financeCategories, setFinanceCategories] = useState([]);
  const [vendorBankAccounts, setVendorBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [handleAddNew, setHandleAddNew] = useState(false);
  const [refreshBankList, setRefreshBankList] = useState(false);

  const [formData, setFormData] = useState({
    date: new Date(),
    memo: "",
    entries: [
      {
        selectedCategory: null,
        financeAccount: null,
        dimension2: null,
        accounts: [],
        dimensions: [],
        amount: "",
        quantity: "",
        price: "",
        lineMemo: "",
        serialNumber: "",
      },
    ],
  });

  const [errors, setErrors] = useState({});

  // ---------- Helpers ----------
  const fetchAccountsAndDimensions = (index, categoryId) => {
    if (!categoryId) return;

    ApiFinance()
      .GetFinanceCategoryAccountsById(categoryId)
      .then((res) => {
        setFormData((prev) => {
          const updated = prev.entries.map((entry, idx) => {
            if (idx === index) {
              return {
                ...entry,
                accounts: res.data || [],
                financeAccount: res.data?.[0] || null,
              };
            }
            return entry;
          });
          return { ...prev, entries: updated };
        });
      })
      .catch(console.error);

    ApiFinance()
      .GetFinanceCategoryDimensionsById(categoryId)
      .then((res) => {
        setFormData((prev) => {
          const updated = prev.entries.map((entry, idx) => {
            if (idx === index) {
              return {
                ...entry,
                dimensions: res.data || [],
                dimension2: res.data?.[0] || null,
              };
            }
            return entry;
          });
          return { ...prev, entries: updated };
        });
      })
      .catch(console.error);
  };

  // ---------- Handlers ----------
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const getTotalAmount = (entries) => {
    const total = entries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );
    return Number.isInteger(total) ? total.toString() : total.toFixed(2);
  };

  const handleEntryChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedEntries = prev.entries.map((entry, idx) => {
        if (idx === index) {
          const updated = { ...entry, [field]: value };

          if (field === "selectedCategory") {
            updated.financeAccount = null;
            updated.dimension2 = null;
            updated.accounts = [];
            updated.dimensions = [];
            updated.serialNumber = "";
            const isQtyPrice = categorySupportsQtyPrice(value?.label1);
            if (!isQtyPrice) {
              updated.quantity = "";
              updated.price = "";
            }
            if (value?.value) {
              fetchAccountsAndDimensions(index, value.value);
            }
          }

          // Calculate amount if quantity or price changes
          const categoryCode = updated.selectedCategory?.label1;
          if (categorySupportsQtyPrice(categoryCode)) {
            if (field === "quantity" || field === "price" || field === "selectedCategory") {
              const q = parseFloat(updated.quantity);
              const p = parseFloat(updated.price);
              if (!isNaN(q) && !isNaN(p)) {
                updated.amount = (q * p).toString();
              } else {
                updated.amount = "";
              }
            }
          }

          return updated;
        }
        return entry;
      });
      return { ...prev, entries: updatedEntries };
    });
    setErrors((prev) => {
      const updatedEntries = Array.isArray(prev.entries) ? [...prev.entries] : [];
      if (updatedEntries[index]) {
        if (field === "selectedCategory") {
          updatedEntries[index] = {
            ...updatedEntries[index],
            selectedCategory: "",
            financeAccount: "",
            dimension2: "",
            amount: "",
            quantity: "",
            price: "",
            serialNumber: "",
          };
        } else {
          updatedEntries[index] = {
            ...updatedEntries[index],
            [field]: "",
          };
        }
      }
      return { ...prev, entries: updatedEntries };
    });
  };

  const addEntry = () => {
    const defaultCategory = financeCategories[0] || null;
    const nextIndex = formData.entries.length;
    setFormData((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          selectedCategory: defaultCategory,
          financeAccount: null,
          dimension2: null,
          accounts: [],
          dimensions: [],
          amount: "",
          quantity: "",
          price: "",
          lineMemo: "",
          serialNumber: "",
        },
      ],
    }));
    setErrors((prev) => {
      if (Array.isArray(prev.entries)) {
        return {
          ...prev,
          entries: [...prev.entries, {}]
        };
      }
      return prev;
    });
    if (defaultCategory?.value) {
      fetchAccountsAndDimensions(nextIndex, defaultCategory.value);
    }
  };

  const removeEntry = (index) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((_, idx) => idx !== index),
    }));
    setErrors((prev) => {
      if (Array.isArray(prev.entries)) {
        return {
          ...prev,
          entries: prev.entries.filter((_, idx) => idx !== index)
        };
      }
      return prev;
    });
  };

  // Helper function to pad numbers to 4 digits with leading zeros
  const padTo4Digits = (value) => {
    if (!value) return "0000";
    return String(value).padStart(4, "0");
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const isValid = validate();
    if (!isValid) {
      return;
    }
    const totalAmount = getTotalAmount(formData.entries);

    const entriesPayload = formData.entries.map((entry) => {
      const categoryCode = entry.selectedCategory?.label1;
      const isQtyPrice = categorySupportsQtyPrice(categoryCode);
      const fallbackMemo = entry.lineMemo || formData.memo;
      return {
        category: entry.selectedCategory ? {
          value: entry.selectedCategory.value,
          label: entry.selectedCategory.label,
          label1: entry.selectedCategory.label1
        } : null,
        financeAccount: entry.financeAccount ?? null,
        dimension2: entry.dimension2 ?? null,
        amount: entry.amount,
        lineMemo: isQtyPrice
          ? `${padTo4Digits(entry.quantity)} x ${padTo4Digits(entry.price)}${fallbackMemo ? ` (${fallbackMemo})` : ""}`
          : fallbackMemo,
      };
    });

    const loanRow = formData.entries.find(e => e.selectedCategory?.label1 === "return_loan_individual");
    const serialNumber = loanRow ? loanRow.serialNumber : "";

    const showBankAccountField = checkShowBankAccountField(formData.entries);

    const payload = {
      financeAccount: entriesPayload[0]?.financeAccount ?? null,
      dimension2: entriesPayload[0]?.dimension2 ?? null,
      category: entriesPayload[0]?.category ?? null,
      date: formData.date,
      amount: totalAmount,
      serialNumber: serialNumber,
      memo: formData.memo,
      unitId: loggedInUnitId,
      entries: entriesPayload,
      rafiqBankId: showBankAccountField && selectedBankAccount ? selectedBankAccount.value : 0,
    };

    setLoading((prev) => ({ ...prev, submit: true }));
    ApiFinanceLocal()
      .AddExpenseFromAmanat(payload)
      .then((res) => {
        const data = res.data;

        if (data?.statusCode === "200") {
          swal({
            title: "Success",
            text: t(data.message) || t("funds_expense_added_successfully"),
            icon: "success",
            buttons: "OK",
          }).then(() => {
            history.goBack();
          });
          return;
        }

        swal({
          title: "Error",
          text: data?.message || "Failed to add Expense",
          icon: "error",
          buttons: "OK",
        });

        setLoading((prev) => ({ ...prev, submit: false }));
      })
      .catch(() => {
        // 🚨 ONLY network / server crash comes here
        swal({
          title: "Error",
          text: "Unable to connect to server. Please try again.",
          icon: "error",
          buttons: "OK",
        });
        setLoading((prev) => ({ ...prev, submit: false }));
      });
  };

  // Fetch finance categories on mount
  useEffect(() => {
    setLoading((prev) => ({ ...prev, source: true }));
    ApiFinance()
      .GetFinanceAccountCategoriesListByType("expense")
      .then((res) => {
        const filtered = (res.data || []).filter(
          (category) => category.label1 !== "maktaba"
        );
        const formatted = filtered.map((c) => ({
          value: c.value || c.id,
          label: c.label || c.category,
          label1: c.label1 || c.code,
        }));
        setFinanceCategories(formatted);

        if (formatted.length > 0) {
          const defaultCategory = formatted[0];
          setFormData((prev) => ({
            ...prev,
            entries: [
              {
                selectedCategory: defaultCategory,
                financeAccount: null,
                dimension2: null,
                accounts: [],
                dimensions: [],
                amount: "",
                quantity: "",
                price: "",
                lineMemo: "",
                serialNumber: "",
              },
            ],
          }));
          fetchAccountsAndDimensions(0, defaultCategory.value);
        }
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Failed to load categories",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, source: false })));
  }, [unitTypeName]);

  useEffect(() => {
    ApiFinanceLocal()
      .GetVendorBankAccounts()
      .then((res) => {
        setVendorBankAccounts(res.data || []);
        if (res.data && res.data.length > 0) {
          setSelectedBankAccount((prev) => {
            const stillExists = res.data.find((x) => x.value === prev?.value);
            return stillExists || res.data[0];
          });
        } else {
          setSelectedBankAccount(null);
        }
      })
      .catch(console.error)
      .finally(() => setRefreshBankList(false));
  }, [refreshBankList]);

  const handleAddedBankAccount = (addedAccount) => {
    setSelectedBankAccount(addedAccount);
    setRefreshBankList(true);
  };

  // ---------- Validation ----------
  const validate = () => {
    const temp = {};

    const entriesErrors = [];
    let hasInvalidEntry = false;

    for (let i = 0; i < formData.entries.length; i++) {
      const entry = formData.entries[i];
      const entryErrors = {};

      if (!entry.selectedCategory) {
        entryErrors.selectedCategory = t("common_field_required_error");
        hasInvalidEntry = true;
      } else {
        const categoryCode = entry.selectedCategory.label1;
        const isSpecial = isSpecialCategory(categoryCode);
        const isQtyPrice = categorySupportsQtyPrice(categoryCode);

        // Validate Amount: empty vs <= 0 (only when not auto-calculated)
        if (!isQtyPrice) {
          if (!entry.amount || entry.amount.trim() === "") {
            entryErrors.amount = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedAmount = parseFloat(entry.amount);
            if (isNaN(parsedAmount) || parsedAmount <= 0) {
              entryErrors.amount = t("amount_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }
        }

        if (!isSpecial) {
          if (!entry.financeAccount) {
            entryErrors.financeAccount = t("common_field_required_error");
            hasInvalidEntry = true;
          }
          if (!entry.dimension2) {
            entryErrors.dimension2 = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }

        if (isQtyPrice) {
          // Validate Quantity
          if (!entry.quantity || entry.quantity.trim() === "") {
            entryErrors.quantity = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedQty = parseFloat(entry.quantity);
            if (isNaN(parsedQty) || parsedQty <= 0) {
              entryErrors.quantity = t("quantity_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }

          // Validate Price
          if (!entry.price || entry.price.trim() === "") {
            entryErrors.price = t("common_field_required_error");
            hasInvalidEntry = true;
          } else {
            const parsedPrice = parseFloat(entry.price);
            if (isNaN(parsedPrice) || parsedPrice <= 0) {
              entryErrors.price = t("price_must_be_greater_than_zero");
              hasInvalidEntry = true;
            }
          }
        }

        if (categoryCode === "return_loan_individual" || categoryCode === "loan_individual") {
          if (!entry.serialNumber || !entry.serialNumber.trim()) {
            entryErrors.serialNumber = t("common_field_required_error");
            hasInvalidEntry = true;
          }
        }
      }

      entriesErrors.push(entryErrors);
    }

    const showBankAccountField = checkShowBankAccountField(formData.entries);

    if (showBankAccountField && !selectedBankAccount) {
      temp.selectedBankAccount = t("common_field_required_error");
      hasInvalidEntry = true;
    }

    temp.entries = hasInvalidEntry ? entriesErrors : [];
    temp.amount = "";

    setErrors({
      ...temp,
    });

    return !hasInvalidEntry;
  };

  // ---------- Render ----------
  const showBankAccountField = checkShowBankAccountField(formData.entries);

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>
            {<GetLanguageString props="finance_expense_amanat_account" />}
          </h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {loading.source || loading.submit ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              {handleAddNew && (
                <AddVendorBankAccountModal
                  setModal={setHandleAddNew}
                  getAddedBankAccount={handleAddedBankAccount}
                  onDeleteBankAccount={() => setRefreshBankList(true)}
                />
              )}
              <Row>
                <Col md={showBankAccountField ? "2" : "4"}>
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      maxDate={new Date()}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>
                </Col>
                <Col md={showBankAccountField ? "2" : "4"}>
                  <CustomInput
                    name="memo"
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>
                <Col md={showBankAccountField ? "2" : "4"}>
                  <CustomInput
                    name="totalAmount"
                    label="finance_other_income_total_amount"
                    value={getTotalAmount(formData.entries)}
                    readOnly
                    disabled
                    error={errors.amount}
                  />
                </Col>
                {showBankAccountField && (
                  <Col md="6">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="fund_bank_account" />}
                      </Label>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <div style={{ flex: 1 }}>
                          <Select
                            options={vendorBankAccounts}
                            value={selectedBankAccount}
                            onChange={(val) => {
                              setSelectedBankAccount(val);
                              setErrors((prev) => ({ ...prev, selectedBankAccount: "" }));
                            }}
                            placeholder={t("member_select")}
                            classNamePrefix="select"
                          />
                        </div>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => setHandleAddNew(true)}
                          style={{
                            height: "38px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 12px",
                          }}
                        >
                          <FaPlus
                            color="#f7f7f7ff"
                            size={11}
                            style={{ marginRight: 5, marginBottom: 1 }}
                          />
                          {t("tutorial_add_category") || "Add"}
                        </Button>
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: "11.5px", fontStyle: "italic" }}>
                        {<GetLanguageString props="vendor_bank_payment_hint" />}
                      </div>
                      {formData.entries.length > 1 && (
                        <div className="text-warning mt-1" style={{ fontSize: "11.5px", fontWeight: "bold" }}>
                          {<GetLanguageString props="vendor_bank_single_account_warning" />}
                        </div>
                      )}
                      {errors.selectedBankAccount && (
                        <h4 className="text-error">{errors.selectedBankAccount}</h4>
                      )}
                    </FormGroup>
                  </Col>
                )}
              </Row>
              <div className="ibox">
                <div className="ibox-title d-flex justify-content-between align-items-center">
                  <h5>
                    {
                      <GetLanguageString props="finance_other_expense_entry_details" />
                    }
                  </h5>
                  <MdOutlineAddCircleOutline
                    size={25}
                    color="#3ba147"
                    onClick={addEntry}
                    style={{ marginRight: 6, marginBottom: 1, cursor: "pointer" }}
                  />
                </div>
                <div className="ibox-content">
                  {formData.entries.map((entry, index) => {
                    const categoryCode = entry.selectedCategory?.label1;
                    const isLoanIndividual =
                      categoryCode === "return_loan_individual" ||
                      categoryCode === "loan_individual";
                    const isAssetOrMaktaba = categoryCode === "asset" || categoryCode === "maktaba";
                    const isSpecial = isSpecialCategory(categoryCode);
                    const isQtyPrice = categorySupportsQtyPrice(categoryCode);

                    return (
                      <Row key={index} className="mb-3 align-items-start">
                        <Col md>
                          <CustomSelect
                            name={`category-${index}`}
                            label="finance_tab_category"
                            options={financeCategories}
                            value={entry.selectedCategory}
                            onChange={(val) =>
                              handleEntryChange(index, "selectedCategory", val)
                            }
                            error={errors.entries?.[index]?.selectedCategory}
                            placeholder={t("member_select")}
                          />
                        </Col>
                        {isLoanIndividual && (
                          <Col md>
                            <CustomInput
                              name={`serialNumber-${index}`}
                              label="notifications_rafiq_serial_number"
                              value={entry.serialNumber || ""}
                              onChange={(e) =>
                                handleEntryChange(index, "serialNumber", e.target.value)
                              }
                              error={errors.entries?.[index]?.serialNumber}
                            />
                          </Col>
                        )}
                        {!isSpecial && (
                          <Col md>
                            <CustomSelect
                              name={`financeAccount-${index}`}
                              label="funds_expense_head"
                              options={entry.accounts || []}
                              value={entry.financeAccount}
                              onChange={(val) =>
                                handleEntryChange(index, "financeAccount", val)
                              }
                              error={errors.entries?.[index]?.financeAccount}
                              isDisabled={!entry.selectedCategory}
                              placeholder={
                                !entry.selectedCategory
                                  ? t("finance_expense_heads_select")
                                  : (entry.accounts || []).length > 0
                                    ? t("finance_expense_heads_select")
                                    : t("finance_expense_heads_empty")
                              }
                            />
                          </Col>
                        )}
                        {!isSpecial && (
                          <Col md>
                            <CustomSelect
                              name={`dimension2-${index}`}
                              label="funds_expense_sub_head"
                              options={entry.dimensions || []}
                              value={entry.dimension2}
                              onChange={(val) =>
                                handleEntryChange(index, "dimension2", val)
                              }
                              error={errors.entries?.[index]?.dimension2}
                              isDisabled={!entry.selectedCategory}
                              placeholder={
                                !entry.selectedCategory
                                  ? t("finance_expense_sub_heads_select")
                                  : (entry.dimensions || []).length > 0
                                    ? t("finance_expense_sub_heads_select")
                                    : t("finance_expense_sub_heads_empty")
                              }
                            />
                          </Col>
                        )}
                        <Col md={isAssetOrMaktaba ? "1" : null}>
                          <CustomInput
                            name={`entryAmount-${index}`}
                            label="funds_add_fund_amount"
                            value={entry.amount}
                            onChange={(e) =>
                              handleEntryChange(index, "amount", e.target.value)
                            }
                            error={errors.entries?.[index]?.amount}
                            readOnly={isQtyPrice}
                            disabled={isQtyPrice}
                          />
                        </Col>
                        {isAssetOrMaktaba && (
                          <Col md="1">
                            <CustomInput
                              name={`quantity-${index}`}
                              label="finance_other_income_quantity"
                              value={entry.quantity}
                              onChange={(e) =>
                                handleEntryChange(index, "quantity", e.target.value)
                              }
                              error={errors.entries?.[index]?.quantity}
                            />
                          </Col>
                        )}
                        {isAssetOrMaktaba && (
                          <Col md="1">
                            <CustomInput
                              name={`price-${index}`}
                              label="finance_other_income_price"
                              value={entry.price}
                              onChange={(e) =>
                                handleEntryChange(index, "price", e.target.value)
                              }
                              error={errors.entries?.[index]?.price}
                            />
                          </Col>
                        )}
                        <Col md>
                          <CustomInput
                            name={`lineMemo-${index}`}
                            label="transactions_memo"
                            value={entry.lineMemo}
                            onChange={(e) =>
                              handleEntryChange(index, "lineMemo", e.target.value)
                            }
                            error={errors.entries?.[index]?.lineMemo}
                          />
                        </Col>
                        <Col
                          md="1"
                          className="text-center"
                        >
                          <Label className="col-form-label d-block">&nbsp;</Label>
                          {formData.entries.length === 1 ? (
                            <TbTrashOff
                              size={25}
                              color="#a72424"
                              style={{ opacity: 0.5 }}
                            />
                          ) : (
                            <TbTrash
                              size={25}
                              color="#bb0e0e"
                              style={{ cursor: "pointer" }}
                              onClick={() => removeEntry(index)}
                            />
                          )}
                        </Col>
                      </Row>
                    );
                  })}
                </div>
              </div>
              <Row>
                <Col md="9"></Col>
                <Col md="3" className="text-end">
                  {CheckPermission("CanAddFunds") ? (
                    <>
                      <Button color="white" onClick={() => history.goBack()}>
                        {<GetLanguageString props="common_cancel" />}
                      </Button>
                      &nbsp;&nbsp;
                      <Button
                        disabled={Object.values(loading).some(
                          (val) => val === true,
                        )}
                        color="primary"
                        onClick={() => handleSubmit(false)}
                      >
                        {<GetLanguageString props="funds_add_expense" />}
                      </Button>
                    </>
                  ) : null}
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(ExpenseFromAmanat);
